import { Dispatch, SetStateAction } from 'react';
import { defineMessage, defineMessages } from 'react-intl';
import {
  faSortAmountDown,
  faSortAmountDownAlt,
} from '@fortawesome/pro-solid-svg-icons';

import { SelectConfiguredOption } from '@app/hooks';
import { sortByItemsMessages } from '@app/translations';
import {
  HOLDS_AND_CHECKOUTS_TABS,
  HoldsAndCheckoutsTab,
  SORT_DIRECTION,
  SortOption,
} from '@app/constants';

export enum HoldsAndCheckoutsSortField {
  requestDate = 'requestDate',
  dueDate = 'dueDate',
  amount = 'amount',
}

export type QueryConfig = Partial<{
  includeHolds: boolean;
  includeCharges: boolean;
  includeLoans: boolean;
}>;

export interface TabConfig {
  title: ReturnType<typeof defineMessage>;
  documentTitle: ReturnType<typeof defineMessage>;
  queryConfig: Partial<QueryConfig>;
  sortOptions: SortOption[];
}

const pageMessages = defineMessages({
  requestsDocumentTitle: {
    id: 'requests.documentTitle',
    defaultMessage: 'Requests - Requests & loans - EBSCO Locate',
  },
  holdsDocumentTitle: {
    id: 'holds.documentTitle',
    defaultMessage: 'Holds - Holds & checkouts - EBSCO Locate',
  },
  requestsTitle: {
    id: 'requests.tabWithCounter',
    defaultMessage: 'Requests ({value})',
  },
  holdsTitle: {
    id: 'holds.tabWithCounter',
    defaultMessage: 'Holds ({value})',
  },
  loansDocumentTitle: {
    id: 'loans.documentTitle',
    defaultMessage: 'Loans - Requests & loans - EBSCO Locate',
  },
  checkoutsDocumentTitle: {
    id: 'checkouts.documentTitle',
    defaultMessage: 'Checkouts - Holds & checkouts - EBSCO Locate',
  },
  loansTitle: {
    id: 'loans.tabWithCounter',
    defaultMessage: 'Loans ({value})',
  },
  checkoutsTitle: {
    id: 'checkouts.tabWithCounter',
    defaultMessage: 'Checkouts ({value})',
  },
  feesRequestsDocumentTitle: {
    id: 'feesRequests.documentTitle',
    defaultMessage: 'Fees - Requests & loans - EBSCO Locate',
  },
  feesHoldsDocumentTitle: {
    id: 'fees.documentTitle',
    defaultMessage: 'Fees - Holds & checkouts - EBSCO Locate',
  },
  feesTitle: {
    id: 'fees.tabWithCounter',
    defaultMessage: 'Fees ({value})',
  },
});

export const getTabs = (
  selectConfiguredOption: SelectConfiguredOption
): Record<HoldsAndCheckoutsTab, TabConfig> => ({
  [HOLDS_AND_CHECKOUTS_TABS.holds]: {
    documentTitle: selectConfiguredOption(
      pageMessages.requestsDocumentTitle,
      pageMessages.holdsDocumentTitle
    ),
    title: selectConfiguredOption(
      pageMessages.requestsTitle,
      pageMessages.holdsTitle
    ),
    queryConfig: { includeHolds: true },
    sortOptions: [
      {
        label: sortByItemsMessages.publicationYear,
        sortField: HoldsAndCheckoutsSortField.requestDate,
        sortDirection: SORT_DIRECTION.desc,
        icon: faSortAmountDown,
      },
      {
        label: sortByItemsMessages.publicationYear,
        sortField: HoldsAndCheckoutsSortField.requestDate,
        sortDirection: SORT_DIRECTION.asc,
        icon: faSortAmountDownAlt,
      },
    ],
  },
  [HOLDS_AND_CHECKOUTS_TABS.checkouts]: {
    documentTitle: selectConfiguredOption(
      pageMessages.loansDocumentTitle,
      pageMessages.checkoutsDocumentTitle
    ),
    title: selectConfiguredOption(
      pageMessages.loansTitle,
      pageMessages.checkoutsTitle
    ),
    queryConfig: {
      includeCharges: true,
      includeLoans: true,
    },
    sortOptions: [
      {
        label: sortByItemsMessages.dueDate,
        sortField: HoldsAndCheckoutsSortField.dueDate,
        sortDirection: SORT_DIRECTION.asc,
      },
      {
        label: sortByItemsMessages.dueDate,
        sortField: HoldsAndCheckoutsSortField.dueDate,
        sortDirection: SORT_DIRECTION.desc,
      },
    ],
  },
  [HOLDS_AND_CHECKOUTS_TABS.fees]: {
    documentTitle: selectConfiguredOption(
      pageMessages.feesRequestsDocumentTitle,
      pageMessages.feesHoldsDocumentTitle
    ),
    title: pageMessages.feesTitle,
    queryConfig: {
      includeCharges: true,
      includeLoans: true,
    },
    sortOptions: [
      {
        label: sortByItemsMessages.amount,
        sortField: HoldsAndCheckoutsSortField.amount,
        sortDirection: SORT_DIRECTION.desc,
      },
      {
        label: sortByItemsMessages.amount,
        sortField: HoldsAndCheckoutsSortField.amount,
        sortDirection: SORT_DIRECTION.asc,
      },
    ],
  },
});

export type SetTabsCounters = Dispatch<
  SetStateAction<Record<HoldsAndCheckoutsTab, number>>
>;
